/**
 * Matches Screen
 *
 * Listagem e gerenciamento de partidas
 */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Eye,
	Trash2,
	MapPin,
	Users,
	Calendar,
	Clock,
} from 'lucide-react'
import Header from '../organisms/Header'
import DataTable from '../organisms/DataTable'
import FilterBar from '../molecules/FilterBar'
import { ConfirmModal } from '../molecules/Modal'
import Badge from '../atoms/Badge'
import { LoadingScreen } from '../atoms/Spinner'
import { useMatches } from '../../core/admin.hooks'
import { matchesApi } from '../../core/admin.api'
import { ADMIN_ROUTES, MATCH_STATUS_LABELS, MATCH_STATUS_COLORS } from '../../core/admin.constants'
import { formatDate } from '../../core/admin.utils'
import toast from 'react-hot-toast'

export default function MatchesScreen() {
	const navigate = useNavigate()
	const [filters, setFilters] = useState({})
	const [selectedMatch, setSelectedMatch] = useState(null)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	const {
		data,
		loading,
		page,
		setPage,
		setSearch,
		refetch,
	} = useMatches(filters)

	const matches = data?.data || []
	const totalPages = data?.pagination?.totalPages || 1
	const total = data?.pagination?.total || 0

	// Filtros disponíveis
	const filterOptions = [
		{
			key: 'status',
			label: 'Status',
			type: 'select',
			options: [
				{ value: '', label: 'Todos' },
				{ value: 'scheduled', label: 'Agendada' },
				{ value: 'in_progress', label: 'Em andamento' },
				{ value: 'completed', label: 'Concluída' },
				{ value: 'cancelled', label: 'Cancelada' },
			],
		},
		{
			key: 'period',
			label: 'Período',
			type: 'select',
			options: [
				{ value: '', label: 'Todos' },
				{ value: 'today', label: 'Hoje' },
				{ value: 'week', label: 'Esta semana' },
				{ value: 'month', label: 'Este mês' },
			],
		},
	]

	// Colunas da tabela
	const columns = [
		{
			key: 'match',
			label: 'Partida',
			render: (row) => (
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/20 rounded-lg">
						<Calendar className="w-5 h-5 text-primary" />
					</div>
					<div>
						<p className="font-medium text-text">
							{row.club?.name || 'Clube'}
						</p>
						<p className="text-sm text-text-body flex items-center gap-1">
							<Clock className="w-3 h-3" />
							{formatDate(row.date, "dd/MM/yyyy 'às' HH:mm")}
						</p>
					</div>
				</div>
			),
		},
		{
			key: 'location',
			label: 'Local',
			render: (row) => (
				<div className="flex items-center gap-1 text-text-body">
					<MapPin className="w-4 h-4" />
					<span className="truncate max-w-[200px]">
						{row.location?.name || row.address || 'N/A'}
					</span>
				</div>
			),
		},
		{
			key: 'players',
			label: 'Jogadores',
			render: (row) => (
				<div className="flex items-center gap-1 text-text-body">
					<Users className="w-4 h-4" />
					<span>
						{row.confirmedPlayers?.length || 0} / {row.maxPlayers || 22}
					</span>
				</div>
			),
		},
		{
			key: 'status',
			label: 'Status',
			render: (row) => {
				const status = row.status || 'scheduled'
				return (
					<Badge variant={MATCH_STATUS_COLORS[status] || 'gray'}>
						{MATCH_STATUS_LABELS[status] || status}
					</Badge>
				)
			},
		},
		{
			key: 'createdAt',
			label: 'Criada em',
			render: (row) => (
				<span className="text-text-muted text-sm">
					{formatDate(row.createdAt, 'dd/MM/yyyy')}
				</span>
			),
		},
	]

	// Ações do menu
	const getRowActions = (row) => [
		{
			icon: Eye,
			label: 'Ver detalhes',
			onClick: () => navigate(`${ADMIN_ROUTES.MATCHES}/${row._id}`),
		},
		{
			icon: Trash2,
			label: 'Excluir',
			onClick: () => {
				setSelectedMatch(row)
				setShowDeleteModal(true)
			},
			variant: 'danger',
		},
	]

	// Handler para excluir
	const handleDelete = async () => {
		if (!selectedMatch) return

		setActionLoading(true)
		try {
			await matchesApi.delete(selectedMatch._id)
			toast.success('Partida excluída com sucesso')
			setShowDeleteModal(false)
			refetch()
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao excluir partida')
		} finally {
			setActionLoading(false)
		}
	}

	if (loading && !data) {
		return (
			<div className="min-h-screen">
				<Header title="Partidas" />
				<LoadingScreen />
			</div>
		)
	}

	return (
		<div className="min-h-screen">
			<Header
				title="Partidas"
				subtitle={`${total} partidas cadastradas`}
			/>

			<div className="p-3 md:p-6 space-y-3 md:space-y-4">
				{/* Filtros */}
				<FilterBar
					filters={filterOptions}
					values={filters}
					onChange={setFilters}
					onSearch={setSearch}
					searchPlaceholder="Buscar por clube, local..."
				/>

				{/* Tabela */}
				<DataTable
					columns={columns}
					data={matches}
					loading={loading}
					emptyMessage="Nenhuma partida encontrada"
					page={page}
					totalPages={totalPages}
					onPageChange={setPage}
					actions={getRowActions}
				/>
			</div>

			{/* Modal de confirmação de exclusão */}
			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleDelete}
				title="Excluir Partida"
				message={`Tem certeza que deseja excluir esta partida do clube "${selectedMatch?.club?.name}"? Esta ação não pode ser desfeita.`}
				confirmText="Excluir"
				variant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}
